import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useJiraWorkHealth } from "@/hooks";
import {
	IMPORT_FAILED_ACTION,
	lastChangeLine,
	matchedSummary,
	projectLine,
	unmatchedRows,
	workHealthErrorMessage,
	workNotices,
} from "@/lib/jiraWorkHealth";
import type { JiraProjectHealth, JiraWorkHealth } from "@/types/api";

/**
 * What Curo found in the approved projects: open work per project, and how many of the people assigned to it Curo
 * matched to a seat, with the reason and the fix for the rest. Counts only, never who: admins see the names in
 * Jira already. Admins only, as the Gateway serves it to admins only.
 */
export function JiraWorkHealthSection() {
	const health = useJiraWorkHealth();
	return (
		<div className="flex flex-col gap-3 rounded-md border border-divider p-3">
			<div>
				<p className="text-caption font-medium text-heading">Work and people</p>
				<p className="text-caption text-muted-foreground">
					Who Curo could match to their Jira tickets, as counts. Names stay in
					Jira.
				</p>
			</div>
			{health.isPending ? (
				<Skeleton className="h-20 w-full" />
			) : health.isError ? (
				<div className="flex flex-wrap items-center gap-3">
					<p className="text-caption text-error">
						{workHealthErrorMessage(health.error)}
					</p>
					<Button variant="outline" onClick={() => health.refetch()}>
						Retry
					</Button>
				</div>
			) : (
				<WorkHealthBody health={health.data} />
			)}
		</div>
	);
}

export function WorkHealthBody({ health }: { health: JiraWorkHealth }) {
	if (health.projects.length === 0)
		return (
			<p className="text-caption text-muted-foreground">
				Approve a project above to see its work here.
			</p>
		);
	return (
		<>
			<ProjectLines projects={health.projects} />
			<People health={health} />
			<Notices notices={workNotices(health.work)} />
			<LastChange line={lastChangeLine(health.work.lastChangeAt)} />
		</>
	);
}

function ProjectLines({ projects }: { projects: JiraProjectHealth[] }) {
	return (
		<ul className="flex flex-col gap-1">
			{projects.map((project) => (
				<li
					key={project.id}
					className="flex flex-wrap items-center gap-2 text-caption"
				>
					<span className="font-medium text-heading">{project.name}</span>
					<span className="text-muted-foreground">
						{projectLine(project.openTickets, project.assignedTickets)}
					</span>
					{project.importing ? (
						<Badge variant="invited">Importing</Badge>
					) : null}
					{project.importFailed ? (
						<>
							<Badge variant="critical">Import stopped</Badge>
							<span className="w-full text-muted-foreground">
								{IMPORT_FAILED_ACTION}
							</span>
						</>
					) : null}
				</li>
			))}
		</ul>
	);
}

function People({ health }: { health: JiraWorkHealth }) {
	const rows = unmatchedRows(health.people);
	return (
		<div className="flex flex-col gap-2">
			<p className="text-caption text-heading">
				{matchedSummary(health.people)}
			</p>
			{rows.length > 0 ? (
				<ul className="flex flex-col gap-2">
					{rows.map((row) => (
						<li key={row.outcome} className="text-caption">
							<p className="text-paragraph">{row.label}</p>
							{row.action ? (
								<p className="text-muted-foreground">{row.action}</p>
							) : null}
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}

function Notices({ notices }: { notices: string[] }) {
	if (notices.length === 0) return null;
	return (
		<ul className="flex flex-col gap-1">
			{notices.map((notice) => (
				<li key={notice} className="text-caption text-muted-foreground">
					{notice}
				</li>
			))}
		</ul>
	);
}

function LastChange({ line }: { line: string | null }) {
	if (!line) return null;
	return <p className="text-caption text-muted1">{line}</p>;
}
